// Custom championship belts: a demand-test page under the owner's
// traffic-first policy (.seo/truth.md, PR #44). Belts are not made yet, so the
// page shows no client work and claims no past belt projects; images are
// AI-generated and captioned as such. Price is "quoted per project"; the only
// figure is the trophy 3D-modeling fee, stated as "starts at". Plating is a
// metal layer over resin, never solid metal or real gold. Keyword targets:
// OpenSEO US 2026-11-01 (see .seo/content-ledger.md).
const { pricing } = require('./custom-trophy')

const beltPricing = { modelingFrom: pricing.modelingFee }

const championshipBelt = {
  path: '/services/custom-trophy/championship-belt/',
  reviewedDate: '2026-11-01',
  reviewedDateLabel: 'November 1, 2026',
  title: 'Custom Fantasy Football Championship Belts | PrintAWorld',
  description:
    'Custom championship belts for fantasy football, office leagues and tournaments, with a center plate sculpted from your logo in NYC. Quoted per project.',
  answer:
    'PrintAWorld makes custom championship belts in New York for fantasy football leagues, office leagues and tournaments. We 3D model the center plate and side plates from your logo or idea, resin print them, paint or metal-plate them, and mount them on a strap with a name plate for every champion. Each belt is quoted per project.',
}

// AI-generated reference images (Codex image_gen, 2026-11-01), local webp in
// static/images/championship-belts/. Captions say they are AI-generated.
const images = {
  goldCenterPlate: {
    file: 'gold-center-plate',
    alt: 'Custom championship belt with a gold-finish sculpted center plate showing a football inside a laurel wreath, on a black strap',
    caption: 'A sculpted center plate in a gold finish',
  },
  silverSidePlates: {
    file: 'silver-side-plates',
    alt: 'Close-up of blank silver-finish side plates on a black championship belt strap',
    caption: 'Side plates, one for each season’s champion',
  },
  leagueCelebration: {
    file: 'league-celebration',
    alt: 'Friends in a living room cheering as one holds a gold championship belt over his head',
    caption: 'The belt the whole league plays for',
  },
  miniBelt: {
    file: 'mini-belt-stand',
    alt: 'Desk-size mini championship belt with a gold-finish center plate on a clear acrylic stand',
    caption: 'A mini belt for the champion’s desk',
  },
}

const audiences = [
  {
    title: 'Fantasy football leagues',
    body: 'The belt the champion wears to the draft and hands over at the end of the season. A fantasy football championship belt carries your league’s logo, not a stock design every other league owns.',
  },
  {
    title: 'Office and rec leagues',
    body: 'Softball, bowling, golf, kickball, trivia night. A title belt gives a rec league something better to play for than a gift card.',
  },
  {
    title: 'Tournaments and events',
    body: 'Golf outings, cornhole and esports tournaments, charity games. One belt for the winner, or a set for each division.',
  },
  {
    title: 'Gyms and clubs',
    body: 'In-house challenges, member of the month, the record board. A belt on the wall keeps people coming back to take it.',
  },
  {
    title: 'Brand events and sales contests',
    body: 'Your logo on the center plate for a sales leaderboard, a launch or an activation where someone gets crowned on camera.',
  },
]

const ideas = [
  {
    title: 'Your league logo as the center plate',
    body: 'We sculpt the logo in raised relief, so it reads as a real plate rather than a printed sticker. No logo yet? Send the league name and the joke behind it.',
  },
  {
    title: 'A side plate for every champion',
    body: 'Each season’s winner gets a name plate, engraved or printed, with blank plates left for the years ahead.',
  },
  {
    title: 'The inside joke, sculpted',
    body: 'The mascot, the commissioner’s face, the trade nobody forgives. If your league has a running joke, it makes the best center plate.',
  },
  {
    title: 'The last-place belt',
    body: 'The belt nobody wants to wear. Same process as the champion’s, in whatever finish best fits the shame.',
  },
  {
    title: 'Mini belts for each champion',
    body: 'A desk-size copy for every winner to keep while the full belt moves on. It comes from the same 3D model, so the design work is done once.',
  },
]

const steps = [
  'Send your logo, a sketch or an AI image, and tell us about the league.',
  'We 3D model the center plate and side plates around your design.',
  'You approve the 3D model before anything is printed.',
  'We resin print the plates and sand them smooth.',
  'We paint them, or plate them in a real metal finish such as gold or silver.',
  'We mount the plates on a strap and add the champions’ name plates.',
]

const comparison = [
  {
    title: 'Stock replica belts',
    body: 'Template belts sold online cost less and ship fast. You pick a design from a catalog and add your text; the plate shape and artwork are the same ones every other buyer gets.',
  },
  {
    title: 'A fully custom belt',
    body: 'The plates are modeled from your league’s logo or idea, so the belt exists nowhere else. It costs more and takes longer, because every plate is designed and finished for you.',
  },
]

const faqs = [
  {
    question: 'How much does a custom championship belt cost?',
    answer: `Custom belts are quoted per project. Every belt starts with 3D modeling the plates from your logo or idea, and our 3D modeling starts at $${beltPricing.modelingFrom}. Printing, the paint or plated finish, the strap and the name plates are then priced from the design. Your written quote confirms the full price before anything starts.`,
  },
  {
    question: 'Is a gold championship belt solid metal or real gold?',
    answer:
      'No. The plates are printed in resin. A gold or silver look is either paint or metal plating, a real metal layer over the printed plate. Plating looks and feels like metal, but the plate is not solid metal and it is not real gold.',
  },
  {
    question: 'Can you put our league logo on the belt?',
    answer:
      'Yes. Send your league’s logo and we sculpt it in raised relief on the center plate. If you don’t have a logo, send the league name, the colors and any running joke, and we design around those.',
  },
  {
    question: 'Can the belt have name plates for every season’s champion?',
    answer:
      'Yes. Side plates can carry each champion’s name and year, engraved or printed. Tell us how many seasons to plan for and we leave blank plates for future winners.',
  },
  {
    question: 'Can I send an AI-generated image of the belt I want?',
    answer:
      'Yes. Send it with your quote request and we model the plates in 3D from it, then send you the model to approve before printing. A front view of one belt on a plain background, with bold shapes and no fine text, comes out closest to the picture.',
  },
  {
    question: 'Can you make a last-place belt or mini belts too?',
    answer:
      'Yes. A last-place belt follows the same process as the champion’s. Mini belts for each winner reuse the same 3D model at desk size, so you pay for the design work once.',
  },
  {
    question: 'Can the champion actually wear it?',
    answer:
      'Yes, it’s mounted on a strap made to be worn for the photo and passed around the room. Resin plates are lighter than cast metal ones. Tell us the waist size or the look you want and the quote sets the strap length.',
  },
  {
    question: 'When should we order for our draft or championship?',
    answer:
      'As soon as you know the date. A month or more leaves room for the 3D model, your approval and finishing. If your date is closer, ask anyway and we’ll tell you what fits the time.',
  },
  {
    question: 'Can you make just one belt?',
    answer:
      'Yes. One belt is fine and there is no minimum order. If you want a champion’s belt, a last-place belt and minis, we quote them together.',
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States. Pickup from our New York studio is available by appointment.',
  },
]

module.exports = {
  beltPricing,
  championshipBelt,
  images,
  audiences,
  ideas,
  steps,
  comparison,
  faqs,
}
